import { isSameYear } from 'date-fns';
import { formatTime } from '@chatwoot/utils';
import { useLocale } from 'shared/composables/useLocale';

/**
 * Datas e durações dos relatórios no idioma da tela.
 *
 * O `format` do date-fns sem locale e o `formatTime` do @chatwoot/utils escrevem
 * em inglês qualquer que seja o idioma ("Sep 20 - Sep 26, 2026", "20-Sep",
 * "26 Min 1 Sec"). O seletor de período, os eixos dos gráficos e as durações
 * passam todos por aqui, para uma tela não voltar a divergir da outra.
 *
 * O idioma é lido na hora de formatar: o painel monta em inglês e só depois
 * troca para o da conta.
 */
export function useReportFormatter() {
  const { resolvedLocale } = useLocale();

  const dateFormat = (date, options) =>
    new Intl.DateTimeFormat(resolvedLocale.value, options).format(date);

  /**
   * "20 de set. – 26 de set. de 2026". O início leva o ano só quando o período
   * atravessa a virada do ano.
   */
  const dateRange = (start, end) => {
    const withYear = { day: 'numeric', month: 'short', year: 'numeric' };
    const startFormat = isSameYear(start, end)
      ? { day: 'numeric', month: 'short' }
      : withYear;
    return `${dateFormat(start, startFormat)} – ${dateFormat(end, withYear)}`;
  };

  /** Dia e mês, para o eixo do gráfico: "20/09". */
  const shortDate = date =>
    dateFormat(date, { day: '2-digit', month: '2-digit' });

  /** Mês e ano, para o eixo agrupado por mês: "set. de 2026". */
  const monthYear = date =>
    dateFormat(date, { month: 'short', year: 'numeric' });

  // Nome de mês ou de dia que abre um rótulo: só a primeira letra sobe. Dentro da frase o
  // português os escreve em minúsculas, e é assim que o Intl os devolve ("setembro").
  const capitalize = text =>
    text.charAt(0).toLocaleUpperCase(resolvedLocale.value) + text.slice(1);

  /** Nome do mês no começo de um rótulo: "Setembro"; com `short`, "Set.". */
  const monthName = (date, month = 'long') =>
    capitalize(dateFormat(date, { month }));

  /** Dia da semana no começo de um rótulo: "Segunda-feira", "Sábado". */
  const weekday = date => capitalize(dateFormat(date, { weekday: 'long' }));

  /**
   * Dia da semana pelo número que o servidor manda (`dow`, 0 é domingo), o das grades de dia da
   * semana × hora e do CSV delas. O 27 de setembro de 2026 caiu num domingo.
   */
  const weekdayOf = dow => weekday(new Date(2026, 8, 27 + dow));

  /**
   * Iniciais dos dias da semana, de segunda a domingo, a ordem do calendário do seletor:
   * "S T Q Q S S D". O 1º de janeiro de 2024 caiu numa segunda.
   */
  const weekdayInitials = () =>
    Array.from({ length: 7 }, (_, index) =>
      dateFormat(new Date(2024, 0, 1 + index), { weekday: 'narrow' })
    );

  const unit = (value, name) =>
    new Intl.NumberFormat(resolvedLocale.value, {
      style: 'unit',
      unit: name,
      unitDisplay: 'short',
    }).format(value);

  const pair = (major, majorUnit, minor, minorUnit) =>
    minor > 0
      ? `${unit(major, majorUnit)} ${unit(minor, minorUnit)}`
      : unit(major, majorUnit);

  /**
   * "26 min 1 s", "1 h 5 min", "3 dias 4 h". Em inglês fica o formato de
   * sempre ("26 Min 1 Sec"). Nos outros idiomas, o mesmo corte do
   * `formatTime` — as duas maiores unidades —, com o nome da unidade no idioma.
   */
  const duration = timeInSeconds => {
    if (resolvedLocale.value.startsWith('en')) return formatTime(timeInSeconds);

    const seconds = Number(timeInSeconds);
    if (seconds >= 86400) {
      const days = Math.floor(seconds / 86400);
      const hours =
        seconds % 86400 < 3600 || days >= 364
          ? 0
          : Math.floor((seconds % 86400) / 3600);
      return pair(days, 'day', hours, 'hour');
    }
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const minutes =
        seconds % 3600 < 60 ? 0 : Math.floor((seconds % 3600) / 60);
      return pair(hours, 'hour', minutes, 'minute');
    }
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      return pair(minutes, 'minute', Math.floor(seconds % 60), 'second');
    }
    return unit(Math.floor(seconds), 'second');
  };

  return {
    dateRange,
    shortDate,
    monthYear,
    monthName,
    weekday,
    weekdayOf,
    weekdayInitials,
    duration,
  };
}
